import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { SessionCostMetric } from "../types";

interface Props {
  item: SessionCostMetric;
  onClose: () => void;
  onDeleted: (id: number) => void;
}

const formatEur = (value: number) => new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
}).format(value);

export default function DeleteSessionCostModal({ item, onClose, onDeleted }: Props) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !deleting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleting, onClose]);

  const confirm = async () => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteSessionCost(item.id);
      onDeleted(item.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete this cost record. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[rgba(36,28,18,0.58)] backdrop-blur-[3px] z-[400] flex items-center justify-center p-5 animate-fade-in"
      onClick={deleting ? undefined : onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-cost-title"
        aria-describedby="delete-cost-description"
        className="card max-w-[460px] w-full px-7 py-7 animate-scale-in"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <p className="section-label text-error mb-2">Cost telemetry</p>
            <h3 id="delete-cost-title" className="text-error">Remove this cost record?</h3>
          </div>
          <button
            type="button"
            className="bg-transparent border-none text-text-tertiary text-xl cursor-pointer leading-none transition-colors duration-200 hover:text-ink p-1 -mr-1 -mt-1 disabled:opacity-40"
            onClick={onClose}
            disabled={deleting}
            aria-label="Close confirmation"
          >
            ×
          </button>
        </div>

        <p id="delete-cost-description" className="text-text-secondary text-sm leading-relaxed">
          This removes the saved cost telemetry and estimate from the admin dashboard only. The learner’s practice session, transcript, progress, and Google billing records stay unchanged.
        </p>

        <div className="rounded-[10px] bg-surface2 border border-border-soft px-4 py-3 mt-5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-ink">Session #{item.session_id}</span>
            {item.cost_estimate_available && <span className="text-sm font-semibold text-success">{formatEur(item.estimated_live_cost_eur)}</span>}
          </div>
          <p className="text-xs text-text-tertiary mt-1">{item.user_email} · {new Date(item.started_at).toLocaleString()}</p>
        </div>

        {error && <p className="text-error text-xs mt-3">{error}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" className="btn-quiet text-sm" onClick={onClose} disabled={deleting} autoFocus>
            Keep record
          </button>
          <button
            type="button"
            className="btn-primary text-sm px-5 !bg-error !text-white !border-error disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={confirm}
            disabled={deleting}
          >
            {deleting ? "Removing…" : "Remove cost record"}
          </button>
        </div>
      </section>
    </div>
  );
}
